import { useEffect, useState } from 'react';

import { useScrollToError } from '../hooks/useScrollToError.ts';
import { useStableValidateMessage } from '../hooks/useStableValidateMessage.ts';
import '../knowledge-radios.css';
import { getContainerClasses } from '../utils/inputStyles';
import CheckboxItem from './CheckboxItem';
import FieldValidationMessage from './FieldValidationMessage.tsx';

console.log('testing CheckboxGroup.tsx');

interface DataSourceItem {
  key: string;
  text: string;
}

const SOURCES_OF_KNOWLEDGE_VIEW = 'TradingInformation_SourcesOfKnowledge';
const NONE_OF_THE_ABOVE_KEY = 'Kn-50';

interface IProps {
  label: string;
  name: string;
  disabled?: boolean;
  options: DataSourceItem[];
  value: string[];
  viewName?: string;
  updateFieldValue?: (value: string[]) => void;
  triggerFieldChange?: (value: string[]) => void;
  onToggle?: (key: string, checked: boolean) => void;
  validatemessage?: string;
}

function resolveExclusiveSelection(selectedValues: string[], key: string, checked: boolean): string[] {
  if (!checked) {
    return selectedValues.filter((v) => v !== key);
  }

  if (key === NONE_OF_THE_ABOVE_KEY) {
    return [NONE_OF_THE_ABOVE_KEY];
  }

  const withoutNone = selectedValues.filter((v) => v !== NONE_OF_THE_ABOVE_KEY);
  return withoutNone.includes(key) ? withoutNone : [...withoutNone, key];
}

function CheckboxGroup(props: IProps) {
  const [selectedValues, setSelectedValues] = useState<string[]>(props.value);
  const isDisabled = !!props.disabled;
  const validateMessage = useStableValidateMessage(props.validatemessage);
  const hasError = !!validateMessage;
  const rootRef = useScrollToError(validateMessage, [props.value, props.updateFieldValue, props.onToggle]);

  useEffect(() => {
    setSelectedValues(props.value);
  }, [props.value]);

  const handleChange = (key: string, checked: boolean) => {
    if (isDisabled) return;

    const isExclusiveView = props.viewName === SOURCES_OF_KNOWLEDGE_VIEW;

    const newValues = isExclusiveView
      ? resolveExclusiveSelection(selectedValues, key, checked)
      : checked
        ? selectedValues.includes(key)
          ? selectedValues
          : [...selectedValues, key]
        : selectedValues.filter((v) => v !== key);

    setSelectedValues(newValues);

    if (props.onToggle) {
      if (isExclusiveView) {
        const previous = new Set(selectedValues);
        const next = new Set(newValues);

        for (const prevKey of previous) {
          if (!next.has(prevKey)) {
            props.onToggle(prevKey, false);
          }
        }
        for (const nextKey of next) {
          if (!previous.has(nextKey)) {
            props.onToggle(nextKey, true);
          }
        }
        return;
      }

      props.onToggle(key, checked);
      return;
    }

    props.updateFieldValue?.(newValues);
    props.triggerFieldChange?.(newValues);
  };

  const containerClasses = getContainerClasses({});
  const options = Array.isArray(props.options) ? props.options : [];
  const errorClass = hasError ? 'kr-radio-error' : '';

  return (
    <div ref={rootRef} className={containerClasses}>
      {props.label && <label className="!text-base">{props.label}</label>}
      <div className={`radio-list ${errorClass} ${isDisabled ? 'radio-list-disabled' : ''} w-full`}>
        {options.map((option) => (
          <CheckboxItem
            key={option.key}
            id={`${props.name}-${option.key}`}
            checked={selectedValues.includes(option.key)}
            caption={option.text}
            disabled={isDisabled}
            hasError={hasError}
            variant="tile"
            onChange={(checked) => handleChange(option.key, checked)}
          />
        ))}
      </div>
      <FieldValidationMessage message={validateMessage} />
    </div>
  );
}

export default CheckboxGroup;
